import { useSelector } from 'react-redux';
import { cardTypeSelectors } from '../../../../state/normalized/insights/cardTypeSlice';
import { RootState } from '../../../../state/store';
import { CSSProperties, forwardRef, memo, ReactNode } from 'react';
import InsightsChartComponent from '../../../../Molecules/InsightsChart/InsightsChartComponent';
import { chartDataSelectors } from '../../../../state/normalized/insights/chartDataSlice';
import dragHandleIcon from 'assets/icons/drag-handle.svg';
import { IconButton, OverflowMenu } from '@getsynapse/design-system';
import { Card, CardType } from '../../../../utils/customTypes';
import { ViewInsightDataOverflowItem } from './ViewInsightDataOverflowItem';
import { ExportInsightDataOverflowMenu } from './ExportInsightDataOverflowMenu';
import { ExportInsightPdfOverflowMenu } from './ExportInsightPdfOverflowMenu';
import { DeleteCardOverflowItem } from './DeleteCardOverflowItem';
import { useDrillDown } from './hooks/useDrillDown';
import { InsightDrilldownSelectedData } from '../../../../types/store/normalized/insights/insightFilterMetadata';

export interface GridItemData {
  gridId: string;
  cardId: string;
  cardType: CardType;
  cardTypeId: string;
  insightId: string;
  name: string;
  dataUrl: string;
}

type GridItemProps = {
  key: string;
  card: Card;
  canEdit?: boolean;
  style?: CSSProperties;
  className?: string;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  onTouchEnd?: () => void;
  children?: ReactNode;
};

// Compare only fields that affect this item’s content/sizing.
// Ignore x and y so pure position changes don’t re-render the item.
const didCardChange = (prevCard: Card, nextCard: Card) => {
  const prevKey = prevCard.id ?? prevCard.lid;
  const nextKey = nextCard.id ?? nextCard.lid;

  if (prevKey !== nextKey) return true;
  if (prevCard.dashboardId !== nextCard.dashboardId) return true;
  if (prevCard.cardTypeId !== nextCard.cardTypeId) return true;
  if (prevCard.width !== nextCard.width) return true;
  return prevCard.height !== nextCard.height;
};

const areEqualGridItems = (
  prevProps: GridItemProps,
  nextProps: GridItemProps
) => {
  if (didCardChange(prevProps.card, nextProps.card)) return false;
  if (prevProps.canEdit !== nextProps.canEdit) return false;
  if (prevProps.className !== nextProps.className) return false;
  return prevProps.style === nextProps.style;
};

export const GridItemComponent = forwardRef<HTMLDivElement, GridItemProps>(
  (props: GridItemProps, ref) => {
    const {
      key,
      card,
      canEdit,
      onMouseDown,
      onMouseUp,
      onTouchEnd,
      className,
      style,
    } = props;

    const canDrag = canEdit;
    const canDelete = canEdit;

    const { handleDrillDown } = useDrillDown();

    const cardType = useSelector(
      (state: RootState) =>
        // TODO: Handle error case where CardType is not found
        cardTypeSelectors.selectCardTypeById(state, card.cardTypeId)!
    );

    const chartData = useSelector((state: RootState) =>
      chartDataSelectors.selectChartDataById(state, cardType.insightId)
    );

    const chartDataStatus = useSelector((state: RootState) =>
      chartDataSelectors.selectStatus(state, cardType.dataUrl)
    );

    const gridItemData: GridItemData = {
      gridId: key,
      cardType,
      cardTypeId: cardType.id,
      dataUrl: cardType.dataUrl,
      insightId: cardType.insightId,
      name: cardType.name,
      // TODO: If both id and lid are undefined, we are in trouble, it's a bad bug
      cardId: card.id ?? card.lid!,
    };

    const overflowMenu = (
      <OverflowMenu
        menuListProps={{
          'data-cy': `insight-${cardType.id}__actions-popover`,
        }}
        menuButtonProps={{
          'data-cy': `insight-${cardType.id}__actions-popover-trigger`,
        }}
      >
        <>
          <ViewInsightDataOverflowItem
            gridItemData={gridItemData}
            card={card}
          />
          <ExportInsightDataOverflowMenu gridItemData={gridItemData} />
          <ExportInsightPdfOverflowMenu gridItemData={gridItemData} />
          {canDelete && <DeleteCardOverflowItem gridItemData={gridItemData} />}
        </>
      </OverflowMenu>
    );
    return (
      <div
        id={cardType.insightId}
        key={key}
        ref={ref}
        className={`shadow-insight-card rounded flex flex-col bg-neutral-white py-2 px-4 relative group ${className}`}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        style={{ ...style }}
      >
        <div className='w-full flex justify-between mb-2'>
          <div className='font-bold'>{cardType.name}</div>
          {canDrag && (
            <div className='absolute top-2 w-full flex justify-center opacity-0 group-hover:opacity-100 transition-opacity'>
              <IconButton
                src={dragHandleIcon}
                size='small'
                variant='tertiary'
                className='drag-handle cursor-move'
              />
            </div>
          )}
          {overflowMenu}
        </div>
        <div className='flex-1 relative'>
          <InsightsChartComponent
            chartData={chartData}
            chartDataStatus={chartDataStatus}
            onDrillDown={(
              drilldownKey?: string,
              data?: InsightDrilldownSelectedData
            ) => {
              handleDrillDown(cardType, drilldownKey, data);
            }}
            cardName={cardType.name}
            insightId={cardType.insightId}
          />
        </div>
      </div>
    );
  }
);

export const GridItem = memo(GridItemComponent, areEqualGridItems);
